import { Eye, EyeOff, ArrowLeft, Mail } from "lucide-react";
import { useAuthStore } from "@/store/auth/auth.store";
import { useState } from "react";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import { REACT_APP_OAUT_GOOGLE_SIGNUP_URL } from "@/config";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { PATH_FORGOT_PASSWORD } from "@/routes/paths";
import { AxiosError } from "axios";
import { checkLoginMethod, signup } from "@/services/auth.service";
import { setAuthToken, setRefreshToken } from "@/utils/axios-instance";
import { getErrorFromBackend } from "@/utils/errors";

type AuthStep = "entry" | "google" | "password" | "signup";

const GOOGLE_SVG = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_login_google)">
      <path d="M20.001 10.2216C20.0131 9.53416 19.9407 8.84776 19.7853 8.17725H10.2051V11.8883H15.8286C15.7221 12.539 15.4823 13.1618 15.1238 13.7194C14.7653 14.2769 14.2955 14.7577 13.7425 15.1327L13.7229 15.257L16.7522 17.5567L16.9619 17.5772C18.8892 15.8328 20.0006 13.266 20.0006 10.2216" fill="#4285F4" />
      <path d="M10.2035 20.0001C12.9585 20.0001 15.2715 19.1111 16.961 17.5778L13.741 15.1332C12.8793 15.7223 11.7229 16.1334 10.2035 16.1334C8.91317 16.126 7.65795 15.7206 6.61596 14.9747C5.57397 14.2287 4.79812 13.1802 4.39848 11.9778L4.2789 11.9878L1.12906 14.3766L1.08789 14.4888C1.93622 16.1457 3.23812 17.5387 4.84802 18.512C6.45791 19.4852 8.31234 20.0005 10.2039 20.0001" fill="#34A853" />
      <path d="M4.39899 11.9777C4.1758 11.3411 4.06063 10.673 4.05807 9.99996C4.06218 9.32799 4.1731 8.66075 4.38684 8.02225L4.38115 7.88968L1.19269 5.4624L1.0884 5.51101C0.372763 6.90343 0 8.4408 0 9.99987C0 11.5589 0.372763 13.0963 1.0884 14.4887L4.39899 11.9777Z" fill="#FBBC05" />
      <path d="M10.2039 3.86663C11.6661 3.84438 13.0802 4.37803 14.1495 5.35558L17.0294 2.59996C15.1823 0.901848 12.7364 -0.0298855 10.2039 -3.6784e-05C8.31236 -0.000477834 6.45795 0.514732 4.84805 1.48798C3.23816 2.46124 1.93625 3.85416 1.08789 5.51101L4.3875 8.02225C4.79107 6.82005 5.5695 5.77231 6.61303 5.02675C7.65655 4.28118 8.91255 3.87541 10.2039 3.86663Z" fill="#EB4335" />
    </g>
    <defs>
      <clipPath id="clip0_login_google">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const LoginForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { status } = useAuthStore();

  const [step, setStep] = useState<AuthStep>("entry");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [signupErrors, setSignupErrors] = useState({ firstName: "", lastName: "", password: "" });

  const resetFlow = () => {
    setStep("entry");
    setPassword("");
    setFirstName("");
    setLastName("");
    setEmailError("");
    setPasswordError("");
    setSignupErrors({ firstName: "", lastName: "", password: "" });
    setShowPassword(false);
  };

  const validateEmail = (val: string) => {
    if (!val.trim()) return "Please enter your email.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val.trim())) return "Please enter a valid email address.";
    return "";
  };

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) { setEmailError(err); return; }
    setEmailError("");
    setCheckingEmail(true);
    try {
      const method = await checkLoginMethod(email.trim());
      setStep(method);
    } catch {
      enqueueSnackbar("Unable to check email. Please try again.", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    } finally {
      setCheckingEmail(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) { setPasswordError("Please enter your password."); return; }
    setPasswordError("");
    try {
      await useAuthStore.getState().login({ email: email.trim(), password });
      enqueueSnackbar("Login Successfully", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      const hasServerResponse = axiosError?.response || axiosError?.status;
      const isNetworkError =
        !navigator.onLine ||
        axiosError?.code === "ERR_NETWORK" ||
        axiosError?.message === "Network Error" ||
        (!hasServerResponse &&
          (axiosError?.message?.toLowerCase().includes("network") ||
            axiosError?.message?.toLowerCase().includes("timeout")));
      enqueueSnackbar(
        isNetworkError
          ? "No internet connection. Please check your network and try again."
          : "Invalid credentials",
        { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
      );
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = {
      firstName: !firstName.trim() ? "First name is required." : "",
      lastName: !lastName.trim() ? "Last name is required." : "",
      password: !password.trim() ? "Password is required." : "",
    };
    setSignupErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    setIsSubmitting(true);
    try {
      const { AccessToken, RefreshToken } = await signup({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      setAuthToken(AccessToken);
      setRefreshToken(RefreshToken);
      enqueueSnackbar("Account created successfully", { variant: "success" });
      useAuthStore.getState().userLoggedIn();
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleRedirect = () => {
    if (REACT_APP_OAUT_GOOGLE_SIGNUP_URL) {
      window.location.href = REACT_APP_OAUT_GOOGLE_SIGNUP_URL;
    }
  };

  return (
    <div className="space-y-4">
      {step !== "entry" && (
        <button
          type="button"
          onClick={resetFlow}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}

      {step !== "entry" && (
        <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-gray-300 bg-gray-50 px-3 py-1.5">
          <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="truncate text-sm text-gray-600">{email}</span>
        </div>
      )}

      {step === "entry" && (
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={handleGoogleRedirect}
            className="mb-2 flex gap-3 w-full cursor-pointer items-center justify-center rounded-md border border-stroke text-base font-medium text-black"
          >
            {GOOGLE_SVG}
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 py-1">
            <div className="h-px flex-1 bg-gray-300" />
            <span className="text-sm font-medium text-gray-500">OR</span>
            <div className="h-px flex-1 bg-gray-300" />
          </div>

          <form onSubmit={handleEmailContinue} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-800" htmlFor="email-input">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                id="email-input"
                type="email"
                placeholder="Enter your email"
                className="bg-white text-black border-gray-400"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
              />
              {emailError && <p className="text-xs text-red-500">{emailError}</p>}
            </div>
            <Button
              className="w-full bg-sky-600 hover:bg-sky-800"
              type="submit"
              disabled={checkingEmail}
            >
              {checkingEmail ? "Checking…" : "Continue"}
            </Button>
          </form>
        </>
      )}

      {step === "google" && (
        <div className="space-y-4">
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
            <p className="text-sm text-sky-800">
              This email is linked to a Google account. Please continue with Google to sign in — no password needed.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={handleGoogleRedirect}
            className="flex gap-3 w-full cursor-pointer items-center justify-center rounded-md border border-stroke text-base font-medium text-black"
          >
            {GOOGLE_SVG}
            Continue with Google
          </Button>
        </div>
      )}

      {step === "password" && (
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-800" htmlFor="password-input">
                Password <span className="text-red-500">*</span>
              </label>
              <Link className="text-xs text-[#3758F9]" to={PATH_FORGOT_PASSWORD}>
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password-input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="bg-white border-gray-400 text-gray-800 pr-10"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {passwordError && <p className="text-xs text-red-500">{passwordError}</p>}
          </div>
          <Button
            className="w-full bg-sky-600 hover:bg-sky-800"
            type="submit"
            disabled={status === "PENDING"}
          >
            Log In
          </Button>
        </form>
      )}

      {step === "signup" && (
        <form onSubmit={handleSignupSubmit} className="space-y-4" noValidate>
          <div className="flex flex-col md:flex-row gap-2 w-full items-start">
            <div className="w-full space-y-1">
              <label className="text-sm font-medium text-gray-800" htmlFor="firstName-input">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="firstName-input"
                className="bg-white text-black border-gray-400"
                placeholder="Enter your First Name"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setSignupErrors(prev => ({ ...prev, firstName: "" })); }}
              />
              {signupErrors.firstName && <p className="text-xs text-red-500">{signupErrors.firstName}</p>}
            </div>
            <div className="w-full space-y-1">
              <label className="text-sm font-medium text-gray-800" htmlFor="lastName-input">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="lastName-input"
                className="bg-white text-black border-gray-400"
                placeholder="Enter your Last Name"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setSignupErrors(prev => ({ ...prev, lastName: "" })); }}
              />
              {signupErrors.lastName && <p className="text-xs text-red-500">{signupErrors.lastName}</p>}
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-800" htmlFor="signup-password-input">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                id="signup-password-input"
                className="bg-white border-gray-400 text-gray-800 pr-10"
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setSignupErrors(prev => ({ ...prev, password: "" })); }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {signupErrors.password && <p className="text-xs text-red-500">{signupErrors.password}</p>}
          </div>
          <Button
            className="w-full bg-sky-500 hover:bg-sky-700 !mt-8"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating account…" : "Sign Up"}
          </Button>
        </form>
      )}
    </div>
  );
};

export default LoginForm;
